import React, { useState, useEffect, useRef } from "react";
import { artImage, artThumb } from "../../decks/getCards";
import { primaryType } from "../../decks/cardDetails";
import { motion, useDragControls } from "framer-motion";
import {
  modifyAtk,
  modifyDef,
  setCurrentCard,
  modifyCounter,
  setEngaged,
  placeToFieldFromHand,
  placeToCemeteryFromHand,
  placeToTopOfDeckFromHand,
  placeToBotOfDeckFromHand,
  playCardToSharedZone,
} from "../../redux/CardSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  fieldIndexAt,
  isOverCemetery,
  isOverHand,
  isOverPlayedZone,
  deckHalfAt,
  fieldSlotCenter,
  setDragHover,
  getBoardScale,
} from "../field/handDrag";
import { triggerCardReveal } from "../field/cardRevealBus";
import cancel from "../../assets/logo/cancel.png";
import carrot from "../../assets/logo/carrot.png";
import drive from "../../assets/logo/drive.png";
import atkImg from "../../assets/logo/atk.png";
import defImg from "../../assets/logo/def.png";

import "../../css/Card.css";

// How far (px) a field card must be dragged before it starts moving. Below this
// the gesture is treated as a tap (engage), so tapping stays reliable and only a
// deliberate drag relocates the card.
const FIELD_DRAG_THRESHOLD = 10;

// Large, very transparent +/- targets that overlay each Atk/Def icon and the
// counter badge ("+" over the top half, "−" over the bottom). Green tint =
// increment, red tint = decrement, so the direction is obvious. Only rendered
// for the player's cards.
const statStepBtnStyle = (kind) => ({
  width: "100%",
  height: "100%",
  minWidth: 0,
  padding: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 18,
  fontWeight: 700,
  lineHeight: 1,
  color: "rgba(255, 255, 255, 0.95)",
  background: kind === "inc" ? "rgba(40, 200, 90, 0.55)" : "rgba(230, 55, 55, 0.55)",
  border: `1px solid ${kind === "inc" ? "rgba(60, 220, 110, 0.85)" : "rgba(240, 80, 80, 0.85)"}`,
  borderRadius: 4,
  cursor: "pointer",
  userSelect: "none",
});

export default function Card({
  name,
  idx,
  // setDragging,
  ready,
  setHovering,
  onField = false,
  evolvedUsed = false,
  opponentField = false,
  cardBeneath,
  engaged,
  showAtk,
  showDef,
  atkVal,
  defVal,
  counterVal,
  discountedPlayCost,
  aura,
  bane,
  ward,
  keywords = [],
  inHand = false,
  inHandIndex = -1,
  constraintsRef,
  handDragging = false,
  onCardDragStart,
  onCardDragEnd,
  onFieldDrop,
  hidden = false,
  // Equipment token attached to this field slot (equipField) — rendered as a
  // small badge whose hover previews the equipment card itself.
  equipCard = 0,
}) {
  let numOfCarrots = 0;
  const dispatch = useDispatch();
  const [atk, setAtk] = useState(0);
  const [def, setDef] = useState(0);
  const [counter, setCounter] = useState(0);
  const [hoverInput, setHoverInput] = useState(false);
  // Reveal each +/- button only while the player hovers its own half of the
  // stat icon (top half = increment, bottom half = decrement).
  const [showAtkInc, setShowAtkInc] = useState(false);
  const [showAtkDec, setShowAtkDec] = useState(false);
  const [showDefInc, setShowDefInc] = useState(false);
  const [showDefDec, setShowDefDec] = useState(false);
  const [showCounterInc, setShowCounterInc] = useState(false);
  const [showCounterDec, setShowCounterDec] = useState(false);
  // Keyword status badges show compact (inside the card) by default and expand
  // to large vertical labels beside the card on hover.
  const [kwHover, setKwHover] = useState(false);

  const reduxEnemyCardSelectedInHand = useSelector(
    (state) => state.card.enemyCardSelectedInHand
  );

  // Rarity/art choice: opponent's field cards use the synced enemy art, all
  // other cards (my hand, my field) use my own. Falls back to default art.
  const reduxMyArt = useSelector((state) => state.card.myArt);
  const reduxEnemyArt = useSelector((state) => state.card.enemyArt);
  const artMap = opponentField ? reduxEnemyArt : reduxMyArt;

  const reduxCardSelectedOnField = useSelector(
    (state) => state.card.cardSelectedOnField
  );
  const reduxEnemyCardSelectedOnField = useSelector(
    (state) => state.card.enemyCardSelectedOnField
  );
  const gameMode = useSelector((state) => state.gameState.gameMode);
  // Live field state, so a drag-drop only fills an empty zone (mirrors the
  // click-to-place flow, which also rejects occupied slots).
  const reduxField = useSelector((state) => state.card.field);
  const reduxRoom = useSelector((state) => state.card.room);

  useEffect(() => {
    setAtk(Number(atkVal));
    setDef(Number(defVal));
  }, [atkVal, defVal]);

  useEffect(() => {
    setCounter(Number(counterVal));
  }, [counterVal]);

  // Field drag-to-move uses a manual drag start (dragListener disabled) so we
  // can require a real drag distance before moving — small movements stay taps.
  const dragControls = useDragControls();
  const fieldDragStart = useRef(null);
  // Set true once a field drag crosses the threshold, so the trailing tap/click
  // that fires on release doesn't also engage the card. Reset on next press.
  const suppressTapRef = useRef(false);
  const fieldDraggable = onField && !opponentField && !ready;
  // Evolved cards (have a base beneath) can be dropped to the cemetery/hand/deck
  // — the base returns to that zone and the evolved card flips back into the
  // evolve deck. Tokens and advanced cards remain slot-move-only.
  const isEvoFieldCard = onField && !!cardBeneath && cardBeneath !== 0;
  const isSpecialFieldCard =
    onField &&
    typeof name === "string" &&
    (name.slice(-5) === "TOKEN" || name.slice(-8) === "ADVANCED");
  const isTokenFieldCard =
    onField &&
    typeof name === "string" &&
    name.slice(-5) === "TOKEN";

  const handleTap = () => {
    if (gameMode === "automated") return;
    if (suppressTapRef.current) {
      suppressTapRef.current = false;
      return;
    }
    if (onField && !opponentField && !ready && !hoverInput) {
      dispatch(setEngaged(idx));
    }
  };

  const handleFieldPointerDown = (e) => {
    if (e.button !== 0) return; // left button only; leave right-click for menus
    fieldDragStart.current = { x: e.clientX, y: e.clientY };
    suppressTapRef.current = false;
  };
  const handleFieldPointerMove = (e) => {
    if (!fieldDragStart.current || suppressTapRef.current) return;
    // Only while the left button is held — pointermove also fires on plain
    // hover, which must never start a drag.
    if (e.buttons !== 1) {
      fieldDragStart.current = null;
      return;
    }
    const dx = e.clientX - fieldDragStart.current.x;
    const dy = e.clientY - fieldDragStart.current.y;
    if (Math.hypot(dx, dy) > FIELD_DRAG_THRESHOLD) {
      suppressTapRef.current = true;
      dragControls.start(e);
    }
  };
  const handleFieldPointerUp = () => {
    fieldDragStart.current = null;
  };
  // Base field cards can go to the cemetery, hand, or deck. Evolved cards can
  // too: the base card goes to the chosen zone and the evolved card flips
  // face-down back into the evolve deck (see handleFieldDrop). Only tokens and
  // advanced cards are restricted to slot-to-slot moves...
  const fieldExtraTargets = !isSpecialFieldCard;
  // ...except tokens: dragging one onto the cemetery pile removes it (mirrors
  // the right-click "Remove" action), so the cemetery is an extra drop target
  // just for them.
  const fieldCemeteryTarget = fieldExtraTargets || isTokenFieldCard;
  // Dragging an Equipment token over a follower attaches it — flag the drag so
  // FieldDropHints highlights followers green instead of red.
  const isEquipmentDrag =
    onField &&
    !opponentField &&
    typeof name === "string" &&
    primaryType(name) === "Equipment";
  const handleFieldDragStart = () => {
    setDragHover({
      active: true,
      index: -1,
      cemetery: false,
      hand: false,
      played: false,
      deck: null,
      equip: isEquipmentDrag,
      showCemetery: fieldCemeteryTarget,
      showHand: fieldExtraTargets,
      showDeck: fieldExtraTargets,
      showPlayed: fieldExtraTargets,
    });
  };
  const handleFieldDrag = (_event, info) => {
    // info.point is in the board's local (scaled) space because of the
    // MotionConfig transformPagePoint around the field grid — scale it back up
    // to viewport coords so it matches the field/cemetery/hand/deck rects.
    const s = getBoardScale();
    const x = info.point.x * s;
    const y = info.point.y * s;
    setDragHover({
      active: true,
      index: fieldIndexAt(x, y),
      cemetery: fieldCemeteryTarget && isOverCemetery(x, y),
      hand: fieldExtraTargets && isOverHand(x, y),
      played: fieldExtraTargets && isOverPlayedZone(x, y),
      deck: fieldExtraTargets ? deckHalfAt(x, y) : null,
      equip: isEquipmentDrag,
      showCemetery: fieldExtraTargets,
      showHand: fieldExtraTargets,
      showDeck: fieldExtraTargets,
      showPlayed: fieldExtraTargets,
    });
  };
  const handleFieldDragEnd = (_event, info) => {
    setDragHover({
      active: false,
      index: -1,
      cemetery: false,
      hand: false,
      played: false,
      deck: null,
      showCemetery: false,
      showHand: false,
      showDeck: false,
      showPlayed: false,
    });
    if (!onFieldDrop) return;
    // See handleFieldDrag: convert board-local point back to viewport coords.
    const s = getBoardScale();
    const x = info.point.x * s;
    const y = info.point.y * s;
    const deckHalf = fieldExtraTargets ? deckHalfAt(x, y) : null;
    let dest;
    if (fieldExtraTargets && isOverPlayedZone(x, y)) dest = { type: "played" };
    else if (fieldCemeteryTarget && isOverCemetery(x, y)) dest = { type: "cemetery" };
    else if (fieldExtraTargets && isOverHand(x, y)) dest = { type: "hand" };
    else if (deckHalf) dest = { type: "deck", half: deckHalf };
    else dest = { type: "field", index: fieldIndexAt(x, y) };
    onFieldDrop(idx, dest);
  };

  // ---- drag a hand card onto a field zone or the cemetery ----
  // Dropping over an empty field zone plays the card there; dropping over the
  // cemetery pile discards it; anywhere else snaps it back to the hand
  // (dragSnapToOrigin). These mirror the right-click "Field"/"Cemetery" menu
  // actions and coexist with the click-to-place flow.
  const handleCardDragStart = () => {
    if (onCardDragStart) onCardDragStart();
    setDragHover({
      active: true,
      index: -1,
      cemetery: false,
      hand: false,
      played: false,
      deck: null,
      showCemetery: true,
      showHand: false,
      showDeck: true,
      showPlayed: true,
    });
  };
  const handleCardDrag = (_event, info) => {
    // The hand is scaled by boardScale (see Game.js MotionConfig), so info.point
    // is in hand-local space — scale it back to viewport coords to match the
    // field/cemetery/deck rects.
    const s = getBoardScale();
    const x = info.point.x * s;
    const y = info.point.y * s;
    setDragHover({
      active: true,
      index: fieldIndexAt(x, y),
      cemetery: isOverCemetery(x, y),
      hand: false,
      played: isOverPlayedZone(x, y),
      deck: deckHalfAt(x, y),
      showCemetery: true,
      showHand: false,
      showDeck: true,
      showPlayed: true,
    });
  };
  const handleCardDragEnd = (_event, info) => {
    setDragHover({
      active: false,
      index: -1,
      cemetery: false,
      hand: false,
      played: false,
      deck: null,
      showCemetery: false,
      showHand: false,
      showDeck: false,
      showPlayed: false,
    });
    if (onCardDragEnd) onCardDragEnd();
    // See handleCardDrag: convert hand-local point back to viewport coords.
    const s = getBoardScale();
    const x = info.point.x * s;
    const y = info.point.y * s;
    const deckHalf = deckHalfAt(x, y);
    if (deckHalf === "top") {
      dispatch(placeToTopOfDeckFromHand({ name: name, index: inHandIndex }));
      return;
    }
    if (deckHalf === "bottom") {
      dispatch(placeToBotOfDeckFromHand({ name: name, index: inHandIndex }));
      return;
    }
    if (isOverPlayedZone(x, y)) {
      dispatch(
        playCardToSharedZone({
          card: name,
          index: inHandIndex,
          from: "hand",
        })
      );
      return;
    }
    if (isOverCemetery(x, y)) {
      dispatch(placeToCemeteryFromHand({ name: name, index: inHandIndex }));
      return;
    }
    const dropIndex = fieldIndexAt(x, y);
    if (dropIndex >= 0 && reduxField[dropIndex] === 0) {
      dispatch(
        placeToFieldFromHand({
          card: name,
          indexInHand: inHandIndex,
          index: dropIndex,
        })
      );
      // Reveal only when played to the field (top row, 0-4), not the EX area.
      if (dropIndex < 5)
        triggerCardReveal(name, reduxRoom, dropIndex, fieldSlotCenter(dropIndex));
    }
  };

  // Drag props differ by where the card lives. Hand cards auto-drag (small
  // built-in threshold) and drop to a field zone or the cemetery. Field cards
  // use a manual drag start gated by FIELD_DRAG_THRESHOLD so a tap still
  // engages; they drop to another field slot to reposition.
  let dragProps = {};
  if (inHand && !ready) {
    dragProps = {
      drag: true,
      dragConstraints: constraintsRef,
      dragSnapToOrigin: true,
      dragElastic: 0.15,
      dragMomentum: false,
      whileDrag: { scale: 1.15, zIndex: 9999, cursor: "grabbing" },
      onDragStart: handleCardDragStart,
      onDrag: handleCardDrag,
      onDragEnd: handleCardDragEnd,
    };
  } else if (fieldDraggable) {
    dragProps = {
      drag: true,
      dragListener: false,
      dragControls: dragControls,
      dragSnapToOrigin: true,
      dragElastic: 0.1,
      dragMomentum: false,
      whileDrag: { scale: 1.08, zIndex: 9999, cursor: "grabbing" },
      onPointerDown: handleFieldPointerDown,
      onPointerMove: handleFieldPointerMove,
      onPointerUp: handleFieldPointerUp,
      onDragStart: handleFieldDragStart,
      onDrag: handleFieldDrag,
      onDragEnd: handleFieldDragEnd,
    };
  }

  // Step Atk/Def up or down by `delta` (clamped). Used by the +/- buttons shown
  // in "Modify Atk/Def" mode — bigger, easier-to-hit controls than the old
  // number-input spinners.
  const changeAtk = (delta) => {
    if (opponentField) return;
    const v = Math.max(0, Math.min(99, Number(atk || 0) + delta));
    setAtk(v);
    dispatch(modifyAtk({ value: String(v), index: idx }));
  };
  const changeDef = (delta) => {
    if (opponentField) return;
    const v = Math.max(0, Number(def || 0) + delta);
    setDef(v);
    dispatch(modifyDef({ value: String(v), index: idx }));
  };

  // Step the counter up or down by `delta` (clamped at 0). Stepping to 0
  // removes the badge, so also reset the hover state it would otherwise leave
  // stuck (no mouseleave fires for an unmounted element).
  const changeCounter = (delta) => {
    if (opponentField) return;
    const v = Math.max(0, Number(counter || 0) + delta);
    setCounter(v);
    dispatch(modifyCounter({ value: v, index: idx }));
    if (v === 0) {
      setHoverInput(false);
      setShowCounterInc(false);
      setShowCounterDec(false);
    }
  };

  const handleHoverStart = () => {
    // While any hand card is being dragged, don't let the pointer passing over
    // other cards lift them or hijack the zoom preview — the rest of the hand
    // should stay put.
    if (handDragging) return;
    setKwHover(true);
    if (!ready) {
      setHovering(true);
      if (name.slice(0, 6) === "Carrot" || name === "Drive Point") {
        dispatch(setCurrentCard(cardBeneath));
      } else {
        dispatch(setCurrentCard(name));
      }
    }
  };

  const cardPos = (idx) => {
    if (idx === -1) return -1;
    else if (idx < 5) return idx + 5;
    else return idx - 5;
  };

  const handleHoverEnd = () => {
    setKwHover(false);
    setHovering(false);
  };

  const updateNumberOfCarrots = () => {
    if (name !== 0) {
      if (name === "Carrot") {
        numOfCarrots = 1;
      } else if (Number(name?.slice(-1)) > 0) {
        numOfCarrots = Number(name.slice(-1));
      }
    }
  };
  updateNumberOfCarrots();

  return (
    <>
      <motion.div
        onTap={handleTap}
        // Firefox starts a native HTML image-drag on mousedown+move over an
        // <img>, which preempts framer-motion's pointer-based drag (Chrome
        // suppresses it via the WebKit-only `-webkit-user-drag` in index.css,
        // but Firefox ignores that). draggable={false} here and on each <img>
        // disables the native drag source so click-and-drag works cross-browser.
        // (Note: onDragStart is framer-motion's pan callback, not the DOM event,
        // so it can't be used to preventDefault the native drag — hence the
        // attribute approach.)
        draggable={false}
        {...dragProps}
        // Lift the whole card (and its overflowing keyword labels) above
        // neighbouring cards while hovered so the black boxes aren't clipped.
        style={{
          position: "relative",
          zIndex: kwHover ? 999 : "auto",
          // Cards use the normal cursor on hover (no custom cursor icon).
          cursor: "default",
          // Kept invisible while its "card played" reveal flies to this slot;
          // it fades in once the reveal lands (see PlayReveal / cardRevealBus).
          opacity: hidden ? 0 : 1,
          pointerEvents: hidden ? "none" : undefined,
          transition: "opacity 0.18s ease-out",
        }}
        animate={engaged ? { rotate: -90 } : { rotate: 0 }}
        initial={false}
        onHoverStart={() => handleHoverStart()}
        onHoverEnd={() => handleHoverEnd()}
        className={
          cardPos(reduxCardSelectedOnField) === idx && opponentField
            ? "box2"
            : reduxEnemyCardSelectedOnField === idx && !opponentField
            ? "box2"
            : inHand && reduxEnemyCardSelectedInHand === inHandIndex
            ? "box2"
            : "none"
        }
      >
        {discountedPlayCost != null && (
          <div
            style={{
              position: "absolute",
              top: "25%",
              left: "30%",
              borderRadius: "50px",
              color: "white",
              fontSize: "30px",
              fontFamily: "Noto Serif JP, serif",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              height: "50px",
              width: "50px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            {discountedPlayCost}
          </div>
        )}
        {keywords && keywords.length > 0 && kwHover && (
          // Hover: larger badge. Not engaged -> sits above the card's top edge,
          // spanning the card width. Engaged -> the card is rotated -90°, so we
          // counter-rotate +90° (text reads normally) and push it to the card's
          // visual top.
          <div
            style={
              engaged
                ? {
                    // Card is rotated -90°; its visual top edge is the card's
                    // height (161px), so size the (counter-rotated) bar to that
                    // and stretch the box to span it.
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    width: 161,
                    transform:
                      "translate(-50%, -50%) rotate(90deg) translateY(-90px)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch",
                    gap: 3,
                    zIndex: 20,
                    pointerEvents: "none",
                  }
                : {
                    position: "absolute",
                    bottom: "100%",
                    left: 0,
                    width: "100%",
                    marginBottom: 4,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch",
                    gap: 3,
                    zIndex: 20,
                    pointerEvents: "none",
                  }
            }
          >
            {keywords.map((kw) => (
              <div
                key={kw}
                style={{
                  background: "#000",
                  color: "#fff",
                  fontFamily: "Noto Serif JP, serif",
                  fontSize: 18,
                  fontWeight: 700,
                  lineHeight: 1,
                  letterSpacing: 0.5,
                  padding: "5px 0",
                  textAlign: "center",
                  borderRadius: 4,
                  border: "1px solid rgba(255,255,255,0.9)",
                  whiteSpace: "nowrap",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.7)",
                }}
              >
                {kw}
              </div>
            ))}
          </div>
        )}
        {keywords && keywords.length > 0 && !kwHover && (
          // Default + any engaged state: the original compact badge at the top
          // of the card (rotates with the card when engaged).
          <div
            style={{
              position: "absolute",
              top: 4,
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              zIndex: 20,
              pointerEvents: "none",
            }}
          >
            {keywords.map((kw) => (
              <div
                key={kw}
                style={{
                  background: "#000",
                  color: "#fff",
                  fontFamily: "Noto Serif JP, serif",
                  fontSize: 11,
                  fontWeight: 700,
                  lineHeight: 1,
                  letterSpacing: 0.5,
                  padding: "3px 7px",
                  borderRadius: 3,
                  border: "1px solid rgba(255,255,255,0.85)",
                  whiteSpace: "nowrap",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.7)",
                }}
              >
                {kw}
              </div>
            ))}
          </div>
        )}
        {counterVal > 0 && (
          // Counter badge with the same hover +/- controls as the Atk/Def
          // icons: "+" over the top half, "−" over the bottom (see
          // statStepBtnStyle). Replaces the old invisible number input.
          <div
            style={{
              position: "absolute",
              top: "25%",
              right: "30%",
              borderRadius: "50px",
              color: "white",
              fontSize: "30px",
              fontFamily: "Noto Serif JP, serif",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              height: "50px",
              width: "50px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 90,
            }}
          >
            {counter}
            {!opponentField && (
              <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", zIndex: 100 }}>
                <div
                  onMouseEnter={() => { setHoverInput(true); setShowCounterInc(true); }}
                  onMouseLeave={() => { setHoverInput(false); setShowCounterInc(false); }}
                  onPointerDown={(e) => e.stopPropagation()}
                  style={{ flex: 1, display: "flex" }}
                >
                  {showCounterInc && <button type="button" onClick={() => changeCounter(1)} style={statStepBtnStyle("inc")}>+</button>}
                </div>
                <div
                  onMouseEnter={() => { setHoverInput(true); setShowCounterDec(true); }}
                  onMouseLeave={() => { setHoverInput(false); setShowCounterDec(false); }}
                  onPointerDown={(e) => e.stopPropagation()}
                  style={{ flex: 1, display: "flex" }}
                >
                  {showCounterDec && <button type="button" onClick={() => changeCounter(-1)} style={statStepBtnStyle("dec")}>−</button>}
                </div>
              </div>
            )}
          </div>
        )}
        {(numOfCarrots > 0 && name !== "Carrot") ||
        (name === "Drive Point" && onField) ? (
          <img
            draggable={false}
            style={{ opacity: 1 }}
            height={"100%"}
            src={artThumb(cardBeneath, artMap)}
            onError={(e) => {
              if (e.currentTarget.src.indexOf("/thumbs/") !== -1)
                e.currentTarget.src = artImage(cardBeneath, artMap);
            }}
            alt={name}
          />
        ) : (
          <img
            draggable={false}
            height={"100%"}
            src={artThumb(name, artMap)}
            onError={(e) => {
              if (e.currentTarget.src.indexOf("/thumbs/") !== -1)
                e.currentTarget.src = artImage(name, artMap);
            }}
            alt={name}
          />
        )}

        {showAtk && (
          <div
            style={{
              position: "absolute",
              bottom: 0,
              right: atk > 9 ? "50%" : "60%",
              display: "flex",
              alignItems: "center",
              zIndex: 90,
            }}
          >
            <img draggable={false} height={"40px"} src={atkImg} alt="atk" />
            <span
              style={{
                color: "white",
                fontSize: "25px",
                textShadow: "-1px 1px 0 #000",
                position: "relative",
                top: "50%",
                right: "50%",
              }}
            >
              {atk}
            </span>
            {/* Two stacked hover-zones over the icon: each reveals ONLY its own
                button while hovered. Top = "+" (green), bottom = "−" (red). */}
            {!opponentField && (
              <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", zIndex: 100 }}>
                <div
                  onMouseEnter={() => { setHoverInput(true); setShowAtkInc(true); }}
                  onMouseLeave={() => { setHoverInput(false); setShowAtkInc(false); }}
                  onPointerDown={(e) => e.stopPropagation()}
                  style={{ flex: 1, display: "flex" }}
                >
                  {showAtkInc && <button type="button" onClick={() => changeAtk(1)} style={statStepBtnStyle("inc")}>+</button>}
                </div>
                <div
                  onMouseEnter={() => { setHoverInput(true); setShowAtkDec(true); }}
                  onMouseLeave={() => { setHoverInput(false); setShowAtkDec(false); }}
                  onPointerDown={(e) => e.stopPropagation()}
                  style={{ flex: 1, display: "flex" }}
                >
                  {showAtkDec && <button type="button" onClick={() => changeAtk(-1)} style={statStepBtnStyle("dec")}>−</button>}
                </div>
              </div>
            )}
          </div>
        )}
        {showDef && (
          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: "70%",
              display: "flex",
              alignItems: "center",
              zIndex: 90,
            }}
          >
            <img draggable={false} height={"40px"} src={defImg} alt="def" />
            <span
              style={{
                color: "white",
                fontSize: "25px",
                textShadow: "-1px 1px 0 #000",
                position: "relative",
                top: "50%",
                right: "50%",
              }}
            >
              {def}
            </span>
            {!opponentField && (
              <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", zIndex: 100 }}>
                <div
                  onMouseEnter={() => { setHoverInput(true); setShowDefInc(true); }}
                  onMouseLeave={() => { setHoverInput(false); setShowDefInc(false); }}
                  onPointerDown={(e) => e.stopPropagation()}
                  style={{ flex: 1, display: "flex" }}
                >
                  {showDefInc && <button type="button" onClick={() => changeDef(1)} style={statStepBtnStyle("inc")}>+</button>}
                </div>
                <div
                  onMouseEnter={() => { setHoverInput(true); setShowDefDec(true); }}
                  onMouseLeave={() => { setHoverInput(false); setShowDefDec(false); }}
                  onPointerDown={(e) => e.stopPropagation()}
                  style={{ flex: 1, display: "flex" }}
                >
                  {showDefDec && <button type="button" onClick={() => changeDef(-1)} style={statStepBtnStyle("dec")}>−</button>}
                </div>
              </div>
            )}
          </div>
        )}
        {evolvedUsed && (
          <img
            draggable={false}
            src={cancel}
            alt={"cancel"}
            style={{
              position: "relative",
              height: "100px",
              width: "100px",
              opacity: 0.65,
              left: "7%",
              bottom: "90%",
            }}
          />
        )}
        {numOfCarrots > 0 && onField && (
          <div
            style={{
              width: "50px",
              position: "relative",
              left: "45%",
              bottom: "120%",
              fontFamily: "EB Garamond",
              color: "white",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              borderRadius: "10px",
              border: "4px solid #0000",
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img
              draggable={false}
              src={carrot}
              alt={"carrot"}
              style={{
                height: "20px",
                width: "20px",
              }}
            />
            <div style={{ fontSize: 15 }}>x {numOfCarrots} </div>
          </div>
        )}
        {name === "Drive Point" && onField && (
          <div
            style={{
              width: "50px",
              position: "relative",
              left: "45%",
              bottom: "120%",
              fontFamily: "EB Garamond",
              color: "white",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              borderRadius: "10px",
              border: "4px solid #0000",
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img
              draggable={false}
              src={drive}
              alt={"drive"}
              style={{
                height: "20px",
                width: "20px",
              }}
            />
            <div style={{ fontSize: 15 }}></div>
          </div>
        )}
        {equipCard !== 0 && !!equipCard && onField && (
          <div
            // Equipment badge (same spot as the Carrot/Drive badges). Hovering
            // it swaps the zoom preview to the equipment card; leaving swaps
            // back to whatever this card normally previews (the pointer is
            // still on the card).
            onMouseEnter={() => {
              dispatch(setCurrentCard(equipCard));
              setHovering(true);
            }}
            onMouseLeave={() =>
              dispatch(
                setCurrentCard(
                  (typeof name === "string" &&
                    (name.slice(0, 6) === "Carrot" || name === "Drive Point")
                    ? cardBeneath
                    : name) || name,
                ),
              )
            }
            style={{
              width: "50px",
              position: "relative",
              left: "45%",
              bottom: "120%",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              borderRadius: "10px",
              border: "4px solid #0000",
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img
              draggable={false}
              src={artThumb(equipCard, artMap)}
              onError={(e) => {
                if (e.currentTarget.src.indexOf("/thumbs/") !== -1)
                  e.currentTarget.src = artImage(equipCard, artMap);
              }}
              alt={equipCard}
              style={{
                height: "26px",
                width: "20px",
                objectFit: "cover",
                borderRadius: "4px",
              }}
            />
          </div>
        )}
      </motion.div>
    </>
  );
}
